import { createContext, useContext, useEffect, useState, ReactNode, FC } from 'react';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { firebaseConfig } from '../firebase';

// Initialize Firebase app
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const firestore = getFirestore(app);
const functions = getFunctions(app, 'us-central1');

// Connect to emulators in development
if (process.env.NODE_ENV === 'development') {
  console.log('Connecting to Functions emulator on localhost:5001');
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// Context types
type FirebaseContextType = {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  functions: Functions;
};

type SignInCheckResult = {
  signedIn: boolean;
  user: User | null;
};

type SignInCheckData = {
  status: 'loading' | 'success' | 'error';
  data: SignInCheckResult;
};

const FirebaseContext = createContext<FirebaseContextType | null>(null);
const SignInCheckContext = createContext<SignInCheckData>({
  status: 'loading',
  data: { signedIn: false, user: null },
});

// Provider component
type FirebaseProviderProps = {
  children: ReactNode;
};

export const FirebaseProvider: FC<FirebaseProviderProps> = ({ children }) => {
  const [signInCheck, setSignInCheck] = useState<SignInCheckData>({
    status: 'loading',
    data: { signedIn: false, user: null },
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setSignInCheck({
        status: 'success',
        data: {
          signedIn: !!user,
          user,
        },
      });
    });

    return () => unsubscribe();
  }, []);

  const value: FirebaseContextType = {
    app,
    auth,
    firestore,
    functions,
  };

  return (
    <FirebaseContext.Provider value={value}>
      <SignInCheckContext.Provider value={signInCheck}>
        {children}
      </SignInCheckContext.Provider>
    </FirebaseContext.Provider>
  );
};

// Hooks to replace reactfire hooks
export const useFirebaseApp = (): FirebaseApp => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebaseApp must be used within a FirebaseProvider');
  }
  return context.app;
};

export const useAuth = (): Auth => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useAuth must be used within a FirebaseProvider');
  }
  return context.auth;
};

export const useFirestore = (): Firestore => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirestore must be used within a FirebaseProvider');
  }
  return context.firestore;
};

export const useFunctions = (): Functions => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFunctions must be used within a FirebaseProvider');
  }
  return context.functions;
};

export const useSigninCheck = (): SignInCheckData => {
  return useContext(SignInCheckContext);
};
